import {
  Building2,
  Vote,
  Heart,
  Megaphone,
  Newspaper,
  GraduationCap,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const SectorNavigationSection = () => {
  const sectors = [
    {
      id: "government",
      title: "Government & Public Sector",
      icon: <Building2 className="h-8 w-8" />,
      description:
        "Real-time citizen feedback to improve policy decisions and public services",
      color: "bg-blue-500",
      hoverColor: "hover:bg-blue-600",
      route: "/solutions/government",
    },
    {
      id: "political",
      title: "Political Campaigns & Elected Officials",
      icon: <Vote className="h-8 w-8" />,
      description:
        "Track voter sentiment and engagement throughout election cycles",
      color: "bg-purple-500",
      hoverColor: "hover:bg-purple-600",
      route: "/solutions/political-campaigns",
    },
    {
      id: "ngo",
      title: "NGOs & Advocacy Groups",
      icon: <Heart className="h-8 w-8" />,
      description:
        "Measure public support for causes and optimize advocacy strategies",
      color: "bg-green-500",
      hoverColor: "hover:bg-green-600",
      route: "/solutions/ngos-advocacy",
    },
    {
      id: "corporate",
      title: "Corporate Marketing & Brand Teams",
      icon: <Megaphone className="h-8 w-8" />,
      description:
        "Understand brand perception and market sentiment across regions",
      color: "bg-orange-500",
      hoverColor: "hover:bg-orange-600",
      route: "/solutions/corporate-marketing",
    },
    {
      id: "media",
      title: "Media Organizations & Journalists",
      icon: <Newspaper className="h-8 w-8" />,
      description:
        "Access verified data sources and track story impact in real-time",
      color: "bg-red-500",
      hoverColor: "hover:bg-red-600",
      route: "/solutions/media-journalism",
    },
    {
      id: "academic",
      title: "Academic Researchers & Think Tanks",
      icon: <GraduationCap className="h-8 w-8" />,
      description:
        "Conduct research with comprehensive datasets and verified populations",
      color: "bg-indigo-500",
      hoverColor: "hover:bg-indigo-600",
      route: "/solutions/academic-research",
    },
  ];

  return (
    <section id="sectors" className="py-20 bg-white dark:bg-gray-800">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Choose Your Sector
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Tailored solutions for every industry. Select your sector to see
              how NationCard™ transforms your decision-making process.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sectors.map((sector) => (
              <Link
                key={sector.id}
                to={sector.route}
                className="group relative bg-gray-50 dark:bg-gray-900 rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 dark:border-gray-700 cursor-pointer overflow-hidden"
              >
                {/* Background gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-nation-blue/5 to-nation-teal/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                {/* Icon */}
                <div
                  className={`${sector.color} ${sector.hoverColor} w-16 h-16 rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300 relative z-10`}
                >
                  {sector.icon}
                </div>

                {/* Content */}
                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-nation-blue transition-colors duration-300">
                    {sector.title}
                  </h3>

                  <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                    {sector.description}
                  </p>

                  {/* CTA that appears on hover */}
                  <div className="flex items-center text-nation-teal font-semibold opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                    <span>Learn More</span>
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>

                {/* Decorative elements */}
                <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-nation-blue/10 to-nation-teal/10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute bottom-4 left-4 w-12 h-12 bg-gradient-to-br from-nation-purple/10 to-nation-blue/10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              </Link>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Need a custom solution for your industry?
            </p>
            <button className="bg-nation-blue hover:bg-nation-blue-dark text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg">
              Contact Our Solutions Team
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SectorNavigationSection;
